import { hashPassword } from './hash';
import { apiToken, uid } from './id';
import { applyRemoteAccount, isApiUp, isMysqlApi, loginRemote, pullAccount, syncAccount } from './cloud';
import {
  addUser,
  getSessionUserId,
  getSitesByUser,
  getUserByEmail,
  getUserById,
  rotateApiToken,
  setSessionUserId,
  toPublicUser,
  updateUser,
} from './store';
import type { PublicUser } from './types';

export function getCurrentUser(): PublicUser | null {
  const id = getSessionUserId();
  if (!id) return null;
  const user = getUserById(id);
  return user ? toPublicUser(user) : null;
}

export async function registerUser(email: string, password: string): Promise<PublicUser> {
  const normalized = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
    throw new Error('请输入有效的邮箱地址');
  }
  if (password.length < 6) {
    throw new Error('密码至少 6 位');
  }
  if (!(await isMysqlApi()) && getUserByEmail(normalized)) {
    throw new Error('该邮箱已注册，请直接登录');
  }
  const user = {
    id: uid(),
    email: normalized,
    passwordHash: await hashPassword(password),
    createdAt: new Date().toISOString(),
    apiToken: apiToken(),
  };
  if (await isApiUp()) {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...user, password }),
    });
    const data = await res.json();
    if (res.status === 409) throw new Error(data.message || '该邮箱已注册，请直接登录');
    if (!res.ok) throw new Error(data.message || '注册失败');
    const saved = applyRemoteAccount(
      { ...user, ...(data.user || {}), passwordHash: user.passwordHash },
      data.sites || []
    );
    setSessionUserId(saved.id);
    return toPublicUser(saved);
  }
  addUser(user);
  setSessionUserId(user.id);
  await syncAccount(user, []);
  return toPublicUser(user);
}

export async function loginUser(email: string, password: string): Promise<PublicUser> {
  if (await isMysqlApi()) {
    const remote = await loginRemote(email.trim(), password);
    if (!remote?.user) throw new Error('账号或密码不正确');
    const saved = applyRemoteAccount(remote.user, remote.sites || []);
    setSessionUserId(saved.id);
    return toPublicUser(saved);
  }

  const hash = await hashPassword(password);
  let user = getUserByEmail(email.trim());
  if (user && hash === user.passwordHash) {
    const remote = await pullAccount(user.apiToken);
    if (remote?.user) applyRemoteAccount({ ...user, ...remote.user, passwordHash: user.passwordHash }, remote.sites || []);
    user = getUserByEmail(email.trim()) || user;
    await syncAccount(user, getSitesByUser(user.id));
    setSessionUserId(user.id);
    return toPublicUser(user);
  }

  const remote = await loginRemote(email.trim(), password);
  if (remote?.user) {
    const saved = applyRemoteAccount(remote.user, remote.sites || []);
    setSessionUserId(saved.id);
    return toPublicUser(saved);
  }

  throw new Error('账号或密码不正确');
}

export async function resetPasswordByEmail(email: string, nextPassword: string): Promise<PublicUser> {
  if (nextPassword.length < 6) throw new Error('新密码至少 6 位');
  if (await isApiUp()) {
    const res = await fetch('/api/auth/reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), nextPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || '重置失败');
    const remote = await loginRemote(email.trim(), nextPassword);
    if (remote?.user) {
      const saved = applyRemoteAccount(remote.user, remote.sites || []);
      setSessionUserId(saved.id);
      return toPublicUser(saved);
    }
    throw new Error('密码已重置，请重新登录');
  }
  const user = getUserByEmail(email.trim());
  if (!user) throw new Error('该邮箱尚未注册');
  updateUser(user.id, { passwordHash: await hashPassword(nextPassword) });
  const next = getUserById(user.id)!;
  await syncAccount(next, getSitesByUser(next.id));
  setSessionUserId(user.id);
  return toPublicUser(next);
}

export function logoutUser() {
  setSessionUserId(null);
}

export async function changePassword(userId: string, oldPassword: string, nextPassword: string) {
  const user = getUserById(userId);
  if (!user) throw new Error('请先登录');
  const oldHash = await hashPassword(oldPassword);
  if (oldHash !== user.passwordHash) throw new Error('原密码不正确');
  if (nextPassword.length < 6) throw new Error('新密码至少 6 位');
  if (await isApiUp()) {
    const res = await fetch('/api/auth/password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: user.apiToken, oldPassword, nextPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { message?: string }).message || '修改失败');
  }
  updateUser(userId, { passwordHash: await hashPassword(nextPassword) });
  const next = getUserById(userId);
  if (next) await syncAccount(next, getSitesByUser(userId));
}

export async function refreshApiToken(userId: string): Promise<PublicUser> {
  const prev = getUserById(userId);
  if (!prev) throw new Error('请先登录');
  const oldToken = prev.apiToken;
  rotateApiToken(userId);
  const user = getUserById(userId);
  if (!user) throw new Error('请先登录');
  try {
    await syncAccount(user, getSitesByUser(userId));
  } catch (err) {
    updateUser(userId, { apiToken: oldToken });
    throw err instanceof Error ? err : new Error('刷新 Token 失败');
  }
  return toPublicUser(getUserById(userId)!);
}

export async function updateDisplayName(userId: string, displayName: string): Promise<PublicUser> {
  updateUser(userId, { displayName: displayName.trim() });
  const user = getUserById(userId);
  if (!user) throw new Error('请先登录');
  await syncAccount(user, getSitesByUser(userId));
  return toPublicUser(user);
}
